// The API's maintenance answer, remembered for the banner.
//
// While SMART moves hosts (docs/d1-exit/runbook.md W2–W11) the API refuses every change with
// a 503 whose code is `maintenance` — the retired Worker's freeze build, and batch-worker under the
// `full` hold — and keeps serving GET reads (a read the app sends as a POST, such as the
// campaign overview or a report query, is refused like a change). Each refusal is still the calling screen's error;
// this only remembers that one happened, so the app can say why in one place instead of
// leaving every Save button to explain it. Not server data, so not a query: a tiny
// external store the API client writes and `useMaintenance` reads.

import { useSyncExternalStore } from 'react';

/** How long a refusal keeps the banner up without another one. */
export const MAINTENANCE_SHOWN_MS = 5 * 60 * 1000;

export const MAINTENANCE_CODE = 'maintenance';

let shownUntil = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function arm(ms: number) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    emit();
  }, ms);
}

/** A request was refused with the maintenance code (the API client calls this). */
export function noteMaintenance(now: number = Date.now()): void {
  const wasActive = isMaintenanceActive(now);
  shownUntil = now + MAINTENANCE_SHOWN_MS;
  arm(MAINTENANCE_SHOWN_MS);
  if (!wasActive) emit();
}

/** A change went through: whatever paused them is over. */
export function clearMaintenance(): void {
  if (shownUntil === 0) return;
  shownUntil = 0;
  if (timer) clearTimeout(timer);
  timer = null;
  emit();
}

export function isMaintenanceActive(now: number = Date.now()): boolean {
  return now < shownUntil;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMaintenance(): boolean {
  return useSyncExternalStore(subscribe, () => isMaintenanceActive(), () => false);
}
